import axios from 'axios';
import { useAuthStore } from '../store';
import useShopStore from '../store/useShopStore';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api/v1';

const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000,
});

// Request interceptor - attach auth token and shop header
apiClient.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const user = useAuthStore.getState().user;
    if (user?.role === 'superadmin') {
      const selectedShop = useShopStore.getState().selectedShop;
      if (selectedShop?._id) {
        config.headers['X-Shop-Id'] = selectedShop._id;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const { response, config } = error;

    // Sesion cerrada por el superadmin: el token quedo invalidado en el servidor.
    // Cubre a quien estaba desconectado y no recibio el evento de socket.
    if (response?.status === 401 && response?.data?.code === 'SESSION_INVALIDATED') {
      const isOnLoginPage = window.location.pathname === '/login';
      if (!isOnLoginPage) {
        useAuthStore.getState().logout();
        useShopStore.getState().clearShop();
        window.location.href = '/login?reason=session_ended';
      }
      return Promise.reject(new Error('Su sesión fue cerrada. Vuelva a iniciar sesión.'));
    }

    // Handle 401 - Unauthorized (token expired or invalid)
    // Skip redirect if it's a login attempt or already on login page
    if (response?.status === 401) {
      const isAuthEndpoint = config?.url?.includes('/auth/login');
      const isOnLoginPage = window.location.pathname === '/login';

      if (!isAuthEndpoint && !isOnLoginPage) {
        const logout = useAuthStore.getState().logout;
        useShopStore.getState().clearShop();
        logout();
        window.location.href = '/login';
      }
    }

    // Handle SHOP_INACTIVE - shop deactivated (unpaid)
    if (response?.status === 403 && response?.data?.code === 'SHOP_INACTIVE') {
      const isOnLoginPage = window.location.pathname === '/login';
      const paymentDueDay = response?.data?.paymentDueDay || '';

      if (!isOnLoginPage) {
        // User is inside the system — force logout and redirect
        const logout = useAuthStore.getState().logout;
        useShopStore.getState().clearShop();
        logout();
        window.location.href = `/login?reason=shop_inactive${paymentDueDay ? `&dueDay=${paymentDueDay}` : ''}`;
      }
      // For login attempts: propagate with code so Login.jsx can show the banner
      const err = new Error('SHOP_INACTIVE');
      err.code = 'SHOP_INACTIVE';
      err.paymentDueDay = paymentDueDay;
      return Promise.reject(err);
    }

    // Extract error message
    const message =
      response?.data?.message || error.message || 'Ocurrió un error inesperado';

    return Promise.reject(new Error(message));
  }
);

export default apiClient;
